import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

import SvgLoadingAnimation from "../../components/loading";
import IconButton from "../../components/button/IconButton";
import ConfirmModal from "../../components/modal/ConfirmModal";
import CopyLinkButton from "../../components/copy-link-button/CopyLinkButton";
import ToolTip from "../../components/tooltip/ToolTip";
import {
  BOARD_LINK_PARAMS,
  buildImageBoardItemLink,
  buildImageBoardViewLink,
} from "../../utils/deepLink";
import {
  ImageBoardItem,
  useGetImageBoardItemsQuery,
  useCreateImageBoardItemMutation,
  useUpdateImageBoardItemMutation,
  useDeleteImageBoardItemMutation,
  useGetImageBoardStrokesQuery,
} from "../../app/services/imageBoard";
import { useS3Upload } from "../../hooks/useS3Upload";
import { useUploadFailureDialog } from "../../components/upload-progress/useUploadFailureDialog";
import { isTypingTarget } from "../../utils/keyboardShortcuts";
import { isFileTypeAccepted } from "../../utils/fileType";
import BoardImageItem, { GeometryPatch } from "./BoardImageItem";
import {
  readBoardClipboard,
  writeBoardClipboard,
  type BoardClipboardEntry,
} from "./boardClipboard";
import BoardStrokeLayer from "./BoardStrokeLayer";
import {
  createDrawingSurfaceHandlers,
  PEN_COLORS,
  PEN_WIDTHS,
  useBoardDrawing,
  type BoardTool,
} from "./useBoardDrawing";

// ===== 定数 =====

const MIN_SCALE = 0.1;
const MAX_SCALE = 4;
/** ホイールズームの感度 */
const WHEEL_ZOOM_INTENSITY = 0.01;
/** ツールバーの拡大/縮小ボタン 1 回あたりの倍率 */
const ZOOM_BUTTON_STEP = 1.2;
/** 貼り付け画像の最大辺（ワールド単位） */
const MAX_INITIAL_SIZE = 480;
/** 複数貼り付け時のカスケードオフセット（ワールド単位） */
const CASCADE_OFFSET = 32;
/** 画像の最大サイズ（20MB） */
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
/** 複数選択コピーで合成する PNG の 1 辺の上限（px） */
const COMPOSITE_MAX_DIMENSION = 4096;
/** 複数選択コピーで元画像の解像度を活かすための拡大率の上限 */
const COMPOSITE_MAX_UPSCALE = 4;

// ===== ビューポート =====

/** screen = world * scale + offset */
interface Viewport {
  x: number;
  y: number;
  scale: number;
}

/** アップロード中のプレースホルダー */
interface PendingItem {
  id: string;
  objectUrl: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

const clampScale = (value: number) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

// ===== クリップボードへの書き出し =====
//
// Clipboard API が画像として受け付けるのは実質 PNG 1 枚だけ（`write` の配列に
// 複数 ClipboardItem を渡しても主要ブラウザは先頭以外を無視する）。そのため
// 単独選択は PNG へ正規化し、複数選択は 1 枚へ合成してから書き込む。
//
// この PNG は「他アプリへ持ち出すため」のもので、ボード内での複製には
// 内部クリップボード（boardClipboard.ts）を使う。PNG だとボード上でリサイズした
// サイズが落ちるうえ、貼り付けのたびに S3 へ再アップロードになるため。

const fetchImageBlob = async (url: string): Promise<Blob> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`画像の取得に失敗しました: ${res.status}`);
  return res.blob();
};

const canvasToPng = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("PNG 変換に失敗しました")),
      "image/png",
    );
  });

/** 2D コンテキストを取得する（取れない環境ではコピー自体を失敗させる） */
const getCanvasContext = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("PNG 変換に失敗しました");
  return ctx;
};

/** 画像 1 枚を PNG Blob として取得する（元から PNG ならそのまま使う） */
const fetchAsPng = async (url: string): Promise<Blob> => {
  const blob = await fetchImageBlob(url);
  if (blob.type === "image/png") return blob;
  // canvas が汚染されないよう、URL ではなく取得済み blob からデコードする
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    getCanvasContext(canvas).drawImage(bitmap, 0, 0);
    return await canvasToPng(canvas);
  } finally {
    bitmap.close();
  }
};

/**
 * 複数の画像を、ボード上の相対配置を保ったまま 1 枚の PNG に合成する
 * （「選択範囲のスクリーンショット」に相当）。解像度は元画像を活かせるよう
 * 選択内の最大倍率へ合わせ、巨大化しないよう拡大率と 1 辺の長さに上限を設ける。
 */
const composeItemsToPng = async (targets: ImageBoardItem[]): Promise<Blob> => {
  const loaded = await Promise.all(
    targets.map(async (item) => ({
      item,
      bitmap: await createImageBitmap(await fetchImageBlob(item.image_url)),
    })),
  );
  try {
    const minX = Math.min(...targets.map((i) => i.x));
    const minY = Math.min(...targets.map((i) => i.y));
    const maxX = Math.max(...targets.map((i) => i.x + i.width));
    const maxY = Math.max(...targets.map((i) => i.y + i.height));
    const boxWidth = Math.max(1, maxX - minX);
    const boxHeight = Math.max(1, maxY - minY);

    const naturalRatio = Math.max(
      1,
      ...loaded.map(
        ({ item, bitmap }) => bitmap.width / Math.max(1, item.width),
      ),
    );
    const ratio = Math.min(
      naturalRatio,
      COMPOSITE_MAX_UPSCALE,
      COMPOSITE_MAX_DIMENSION / Math.max(boxWidth, boxHeight),
    );

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(boxWidth * ratio));
    canvas.height = Math.max(1, Math.round(boxHeight * ratio));
    const ctx = getCanvasContext(canvas);
    // 重なりの見え方をボードと一致させるため z_index の昇順で描く
    [...loaded]
      .sort((a, b) => a.item.z_index - b.item.z_index)
      .forEach(({ item, bitmap }) => {
        ctx.drawImage(
          bitmap,
          (item.x - minX) * ratio,
          (item.y - minY) * ratio,
          item.width * ratio,
          item.height * ratio,
        );
      });

    return await canvasToPng(canvas);
  } finally {
    loaded.forEach(({ bitmap }) => bitmap.close());
  }
};

/** 画像ファイルの自然サイズを取得し、最大辺 MAX_INITIAL_SIZE に収める */
const measureImageSize = (
  objectUrl: string,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(
        1,
        MAX_INITIAL_SIZE / Math.max(img.naturalWidth, img.naturalHeight),
      );
      resolve({
        width: Math.max(1, Math.round(img.naturalWidth * ratio)),
        height: Math.max(1, Math.round(img.naturalHeight * ratio)),
      });
    };
    img.onerror = () => reject(new Error("画像の読み込みに失敗しました"));
    img.src = objectUrl;
  });

// ===== スタイル =====

/* 高さは親（共有ノートの本文エリアの残り高さ全部）に追従してキャンバスを
   広げる。本文エリアをフルブリードで使うため、カード的な枠線 / 角丸 / 影は
   持たせない。 */
const BoardWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 320px;
  overflow: hidden;
  background-color: #fcfcfc;
  background-image: radial-gradient(circle, #e2e2e2 1px, transparent 1px);
  touch-action: none;
  outline: none;
  user-select: none;
  cursor: grab;

  &.is-panning {
    cursor: grabbing;
  }
`;

const World = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  transform-origin: 0 0;
`;

const PendingPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  border: 1px dashed var(--soremo-light-gray);
  border-radius: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    opacity: 0.5;
    display: block;
    pointer-events: none;
  }
`;

const EmptyGuide = styled.div.attrs({
  className: "u-col-center u-gap8",
})`
  position: absolute;
  inset: 0;
  justify-content: center;
  pointer-events: none;
`;

// BottomAppBar と重ならないよう、ボード上部中央に置く
// （プロジェクト内では右上にプロジェクトメニューがフロートするため中央寄せ）
const FloatingToolbar = styled.div.attrs({
  className: "u-row-center u-gap8",
})`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
`;

/**
 * 描画モード中に画像レイヤーの上へ被せて、ポインタ入力を横取りする面。
 * これがあると Apple Pencil でなぞっても画像のドラッグや選択が始まらない。
 * 指での操作はフック側で弾いてパン / ピンチへ回す。
 */
const DrawingSurface = styled.div`
  position: absolute;
  inset: 0;
  touch-action: none;
  cursor: crosshair;
`;

/** ペンの色見本。選択中は輪郭を出す。 */
const ColorSwatch = styled.button<{ $color: string; $selected: boolean }>`
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid
    ${({ $selected }) => ($selected ? "var(--soremo-blue)" : "transparent")};
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
  cursor: pointer;
`;

/** ペンの太さ見本。実際の太さを丸の直径で見せる。 */
const WidthSwatch = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? "var(--soremo-border)" : "transparent")};
  cursor: pointer;

  span {
    display: block;
    border-radius: 50%;
    /* --soremo-black は未定義なので使わない（透明になって見えなくなる） */
    background: var(--soremo-gray);
  }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: var(--soremo-border);
`;

const ZoomLabel = styled.button`
  min-width: 48px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: center;
`;

interface ImageBoardProps {
  /** ボードページ（display_style: "board" のノート）の ID */
  noteId: string;
  /** ボードのセクション。画像は purpose=note_board でこのセクションへ上げる */
  sectionId: string;
  /** リンクのラベル（ボードのタイトル） */
  title?: string;
  /** プロジェクト内で開いている場合のプロジェクト（リンクをプロジェクト配下にする） */
  linkProjectId?: string;
  /** 閲覧のみ（公開セクションの非メンバー）。追加・移動・描画・削除をしない */
  readOnly?: boolean;
}

/**
 * 共有ノートのイメージボードページ（Figma/Freeform 簡易版）。
 *
 * - 無限キャンバス: 背景ドラッグ / ホイールでパン、Ctrl(⌘)+ホイール / ピンチでズーム
 * - クリップボード貼り付け（⌘V）・ドラッグ&ドロップ・ファイル選択で画像を追加
 * - 画像はドラッグで移動、四隅ハンドルでリサイズ、選択 + Delete で削除
 * - Shift + クリックで複数選択し、まとめてコピー / 削除できる
 * - ペン / 消しゴムに切り替えると手描きできる（Apple Pencil は筆圧に対応、
 *   指はモード中でもキャンバスのパン / ピンチに使える）。線はメンバー全員で
 *   共有し、Undo で自分が直前に引いた線を取り消せる。Escape で選択モードへ戻る
 * - 選択 + ⌘C（またはツールバー）でクリップボードへ画像をコピー
 *   （複数選択時はボード上の配置を保った 1 枚の PNG に合成する）
 * - 同じボードへの ⌘V は内部クリップボード（boardClipboard.ts）を使い、
 *   コピー元と同じ大きさ・同じ S3 キーで複製する。再アップロードが無いので
 *   即座に貼り付き、同じ画像を何枚置いてもブラウザキャッシュが効く
 * - 変更は即保存され、他メンバーは再表示/フォーカス時に最新化される
 * - ディープリンク: ノートのリンク + `&item={id}` で対象画像へセンタリング + 選択、
 *   `&bx=&by=&bs=` で任意の表示位置（ワールド座標 + ズーム）を復元する。
 *   リンクはツールバーの「リンクをコピー」（選択中の画像 / 現在の表示位置）で
 *   Markdown 形式（[ラベル](URL)）にして発行する
 */
const ImageBoard: React.FC<ImageBoardProps> = ({
  noteId,
  sectionId,
  title,
  linkProjectId,
  readOnly = false,
}) => {
  const { data, isLoading } = useGetImageBoardItemsQuery({ note_id: noteId });
  const [createItem] = useCreateImageBoardItemMutation();
  const [updateItem] = useUpdateImageBoardItemMutation();
  const [deleteItem] = useDeleteImageBoardItemMutation();

  const items: ImageBoardItem[] = useMemo(
    () => data?.data?.items ?? [],
    [data],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  // 選択中アイテム。Shift クリックで複数選択できる（コピー / 削除の対象）。
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  // ===== 手描き（Apple Pencil / マウス） =====
  const [tool, setTool] = useState<BoardTool>("select");
  const [penColor, setPenColor] = useState<string>(PEN_COLORS[0].value);
  const [penWidth, setPenWidth] = useState<number>(PEN_WIDTHS[1]);
  const isDrawingMode = tool !== "select";

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  // 他メンバーの削除などでキャッシュから消えた ID を選択に残さない
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIdSet.has(item.id)),
    [items, selectedIdSet],
  );
  const selectedCount = selectedItems.length;

  const { uploadFiles, clearCompleted, retryFiles, withPostProcess } = useS3Upload({
    purpose: "note_board",
    sectionId,
    skipRegister: true,
    restrictions: { accept: ["image/*"], maxSize: MAX_IMAGE_BYTES },
    onValidationError: (errors) => {
      errors.forEach((err) => toast.error(err.message));
    },
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  /** ワールド原点をキャンバス中央・等倍に戻す */
  const resetViewport = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setViewport({ x: el.clientWidth / 2, y: el.clientHeight / 2, scale: 1 });
  }, []);

  // 初期表示でワールド原点をキャンバス中央に置く
  useLayoutEffect(() => {
    resetViewport();
  }, [resetViewport]);

  /** 指定ワールド座標がキャンバス中央に来るようにパンする（ズーム指定可） */
  const centerOnWorld = useCallback(
    (worldX: number, worldY: number, scale?: number) => {
      const el = containerRef.current;
      if (!el) return;
      const s = clampScale(scale ?? viewportRef.current.scale);
      setViewport({
        x: el.clientWidth / 2 - worldX * s,
        y: el.clientHeight / 2 - worldY * s,
        scale: s,
      });
    },
    [],
  );

  // ── ディープリンク復元 ──
  // `&item={itemId}`（対象画像へセンタリング + 選択）または
  // `&bx=&by=&bs=`（ビューポート中心のワールド座標 + ズーム）を適用する。
  // 適用後はパラメータを外し、以降のパン操作や再訪と衝突させない。
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const itemParam = searchParams.get("item");
    const bx = searchParams.get("bx");
    const by = searchParams.get("by");
    const bs = searchParams.get("bs");
    if (!itemParam && bx == null && by == null) return;

    if (itemParam) {
      // アイテム指定はデータ取得を待ってから解決する
      if (isLoading) return;
      deepLinkAppliedRef.current = true;
      const target = items.find((i) => i.id === itemParam);
      if (target) {
        const el = containerRef.current;
        // 大きな画像でも全体が見えるよう、必要なら縮小して収める（最大等倍）
        const fitScale = el
          ? clampScale(
              Math.min(
                1,
                (el.clientWidth * 0.8) / target.width,
                (el.clientHeight * 0.8) / target.height,
              ),
            )
          : 1;
        centerOnWorld(
          target.x + target.width / 2,
          target.y + target.height / 2,
          fitScale,
        );
        setSelectedIds([target.id]);
      } else {
        toast.error("リンク先の画像が見つかりませんでした");
      }
    } else {
      deepLinkAppliedRef.current = true;
      const x = Number(bx);
      const y = Number(by);
      const scale = bs != null ? Number(bs) : NaN;
      if (Number.isFinite(x) && Number.isFinite(y)) {
        centerOnWorld(x, y, Number.isFinite(scale) ? scale : undefined);
      }
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        BOARD_LINK_PARAMS.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true },
    );
  }, [searchParams, items, isLoading, centerOnWorld, setSearchParams]);

  // ===== 座標変換 =====

  /** スクリーン座標（クライアント座標）→ ワールド座標 */
  const screenToWorld = useCallback((clientX: number, clientY: number) => {
    const el = containerRef.current;
    const vp = viewportRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: (clientX - rect.left - vp.x) / vp.scale,
      y: (clientY - rect.top - vp.y) / vp.scale,
    };
  }, []);

  // ===== 手描きストローク =====

  const { data: strokeData } = useGetImageBoardStrokesQuery({ note_id: noteId });
  const strokes = useMemo(() => strokeData?.data.strokes ?? [], [strokeData]);

  const drawing = useBoardDrawing({
    noteId,
    strokes,
    tool,
    color: penColor,
    width: penWidth,
    scale: viewport.scale,
    screenToWorld,
  });

  /** 現在のビューポート中央のワールド座標 */
  const viewportCenterWorld = useCallback(() => {
    const el = containerRef.current;
    const vp = viewportRef.current;
    const w = el?.clientWidth ?? 0;
    const h = el?.clientHeight ?? 0;
    return {
      x: (w / 2 - vp.x) / vp.scale,
      y: (h / 2 - vp.y) / vp.scale,
    };
  }, []);

  /** 指定スクリーン位置を基準にズームする */
  const zoomAt = useCallback(
    (pivotX: number, pivotY: number, factor: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = pivotX - rect.left;
      const py = pivotY - rect.top;
      setViewport((prev) => {
        const scale = clampScale(prev.scale * factor);
        const ratio = scale / prev.scale;
        return {
          scale,
          x: px - (px - prev.x) * ratio,
          y: py - (py - prev.y) * ratio,
        };
      });
    },
    [],
  );

  /** キャンバス中央を基準にズームする（ツールバーの拡大/縮小ボタン用） */
  const zoomAtCenter = useCallback(
    (factor: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
    },
    [zoomAt],
  );

  // ===== 画像追加（アップロード → 作成） =====

  const addImageFiles = useCallback(
    async (files: File[], dropWorldPos?: { x: number; y: number }) => {
      if (readOnly) return;
      // MIME が空になる端末があるため拡張子も見る。弾いた分は黙って消さず通知する
      const imageFiles = files.filter((f) =>
        isFileTypeAccepted(f, ["image/*"]),
      );
      files
        .filter((f) => !imageFiles.includes(f))
        .forEach((f) =>
          toast.error(`${f.name}: イメージボードには画像のみ追加できます`),
        );
      if (imageFiles.length === 0) return;

      const basePos = dropWorldPos ?? viewportCenterWorld();

      // Phase 1: 画像サイズを測ってプレースホルダを配置する
      interface PreparedImage {
        file: File;
        pendingId: string;
        objectUrl: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }
      const prepared: PreparedImage[] = [];
      await Promise.all(
        imageFiles.map(async (file, index) => {
          const pendingId = crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${index}`;
          const objectUrl = URL.createObjectURL(file);
          try {
            const { width, height } = await measureImageSize(objectUrl);
            const x = basePos.x - width / 2 + index * CASCADE_OFFSET;
            const y = basePos.y - height / 2 + index * CASCADE_OFFSET;
            setPendingItems((prev) => [
              ...prev,
              { id: pendingId, objectUrl, x, y, width, height },
            ]);
            prepared.push({ file, pendingId, objectUrl, x, y, width, height });
          } catch {
            URL.revokeObjectURL(objectUrl);
            toast.error(`${file.name}: 画像を読み込めませんでした`);
          }
        }),
      );
      if (prepared.length === 0) return;

      // Phase 2: まとめてアップロードし、失敗があればダイアログで解決してから登録する
      try {
        const results = await uploadFiles(prepared.map((p) => p.file));
        const hadFailures = results.some((r) => r.status === "error");
        const resolved = await resolveUploadFailures(results);
        if (resolved === null) {
          // キャンセル: ボードへの追加を中断
          clearCompleted();
          return;
        }
        if (hadFailures) clearCompleted();

        const keyByFile = new Map<File, string>();
        resolved.forEach((r) => {
          if (r.fileKey) keyByFile.set(r.file, r.fileKey);
        });

        // S3 転送後のボード登録もクライアント主導のため、
        // 完了するまで「処理中」として扱う（離脱を引き止める）
        await withPostProcess(
          () =>
            Promise.all(
              prepared.map(async (p) => {
                // 「失敗分を除いて続行」やバリデーション除外分はスキップする
                const fileKey = keyByFile.get(p.file);
                if (!fileKey) return;
                try {
                  await createItem({
                    note_id: noteId,
                    image_key: fileKey,
                    x: p.x,
                    y: p.y,
                    width: p.width,
                    height: p.height,
                  }).unwrap();
                } catch {
                  toast.error(`${p.file.name}: 画像の追加に失敗しました`);
                }
              }),
            ),
          "ボードに追加中",
        );
      } finally {
        const ids = new Set(prepared.map((p) => p.pendingId));
        setPendingItems((prev) => prev.filter((p) => !ids.has(p.id)));
        prepared.forEach((p) => URL.revokeObjectURL(p.objectUrl));
      }
    },
    [
      readOnly,
      noteId,
      viewportCenterWorld,
      uploadFiles,
      resolveUploadFailures,
      clearCompleted,
      createItem,
      withPostProcess,
    ],
  );

  // ===== ボード内での複製（内部クリップボード） =====

  /** 直前に貼り付けた内容と回数。同じものを連続で貼るときに少しずつずらす。 */
  const pasteCascadeRef = useRef<{ signature: string; count: number }>({
    signature: "",
    count: 0,
  });

  /**
   * 内部クリップボードのうち、このボードへ今すぐ複製できる分だけを返す。
   *
   * 別ボードの控えは BE の image_key 検証で弾かれるため対象外。
   * 元アイテムが削除されていると S3 の実体も消えているので、現在のボードに
   * 残っているキーだけに絞る。`paste` ハンドラが `preventDefault()` するか
   * どうかを同期的に決められるよう、判定はここだけで完結させる。
   */
  const resolvePastableEntries = useCallback((): BoardClipboardEntry[] => {
    const stored = readBoardClipboard();
    if (readOnly || !stored || stored.boardId !== noteId) return [];
    const liveKeys = new Set(items.map((item) => item.image_key));
    return stored.entries.filter((entry) => liveKeys.has(entry.image_key));
  }, [readOnly, noteId, items]);

  /**
   * 内部クリップボードの控えからボードへ複製する。
   *
   * S3 キーをそのまま使い回すのでアップロードが発生せず即座に貼り付く。
   * 同じ URL になるためブラウザキャッシュにも乗り、同じ画像を何枚並べても
   * 追加のダウンロードとデコードは起きない。
   * 渡す `entries` は `resolvePastableEntries` で絞り込み済みであること。
   */
  const pasteBoardEntries = useCallback(
    async (entries: BoardClipboardEntry[]) => {
      if (readOnly || entries.length === 0) return;

      const boxWidth = Math.max(1, ...entries.map((e) => e.offsetX + e.width));
      const boxHeight = Math.max(1, ...entries.map((e) => e.offsetY + e.height));

      const signature = entries.map((e) => e.image_key).join("|");
      const previous = pasteCascadeRef.current;
      const count = previous.signature === signature ? previous.count + 1 : 0;
      pasteCascadeRef.current = { signature, count };
      const cascade = count * CASCADE_OFFSET;

      const center = viewportCenterWorld();
      const originX = center.x - boxWidth / 2 + cascade;
      const originY = center.y - boxHeight / 2 + cascade;

      // z_index は BE が「現在の最大 + 1」で採番するため、重なり順を保つには
      // 控えた順（コピー時に z_index 昇順で並べてある）に 1 件ずつ作る。
      const createdIds: string[] = [];
      let failed = 0;
      for (const entry of entries) {
        try {
          const res = await createItem({
            note_id: noteId,
            image_key: entry.image_key,
            x: originX + entry.offsetX,
            y: originY + entry.offsetY,
            width: entry.width,
            height: entry.height,
          }).unwrap();
          createdIds.push(res.data.item.id);
        } catch {
          failed += 1;
        }
      }

      if (createdIds.length === 0) {
        toast.error("画像の貼り付けに失敗しました");
        return;
      }
      if (failed > 0) {
        toast.error(`${failed}件の画像を貼り付けられませんでした`);
      }
      setSelectedIds(createdIds);
    },
    [readOnly, noteId, viewportCenterWorld, createItem],
  );

  // クリップボード貼り付け（ボードが表示されている間のみ有効）。
  // 同じボードからのコピーは内部クリップボードを優先し、コピー元と同じ
  // サイズ・同じ S3 キーで複製する。他アプリからの貼り付けや別ボードの
  // 控えは従来どおりファイルとしてアップロードする。
  useEffect(() => {
    if (readOnly) return undefined;
    const handlePaste = (e: ClipboardEvent) => {
      const el = containerRef.current;
      // Activity で hidden の間（display: none）は offsetParent が null になる
      if (!el || el.offsetParent === null) return;
      // 入力欄への貼り付けを妨げない
      if (isTypingTarget(e.target)) return;

      const files = Array.from(e.clipboardData?.files ?? []).filter((f) =>
        f.type.startsWith("image/"),
      );
      const entries = resolvePastableEntries();

      // どちらの経路でも貼れないなら preventDefault せず素通しする。
      if (entries.length === 0 && files.length === 0) return;
      e.preventDefault();

      if (entries.length > 0) pasteBoardEntries(entries);
      else addImageFiles(files);
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [readOnly, addImageFiles, pasteBoardEntries, resolvePastableEntries]);

  // ===== パン / ズーム =====

  // ホイール: パン、Ctrl(⌘)+ホイール / トラックパッドのピンチ: ズーム
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        zoomAt(
          e.clientX,
          e.clientY,
          Math.exp(-e.deltaY * WHEEL_ZOOM_INTENSITY),
        );
      } else {
        setViewport((prev) => ({
          ...prev,
          x: prev.x - e.deltaX,
          y: prev.y - e.deltaY,
        }));
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [zoomAt]);

  // ポインタ管理（背景ドラッグでパン、2 本指でピンチ）
  const activePointersRef = useRef<Map<number, { x: number; y: number }>>(
    new Map(),
  );
  const panPointerRef = useRef<number | null>(null);
  const pinchDistanceRef = useRef<number | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // アイテム上の pointerdown は stopPropagation されるため、ここに来るのは背景のみ
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    containerRef.current?.setPointerCapture(e.pointerId);

    if (activePointersRef.current.size === 1) {
      panPointerRef.current = e.pointerId;
      setIsPanning(true);
      setSelectedIds([]);
    } else if (activePointersRef.current.size === 2) {
      // ピンチ開始（パンは解除）
      panPointerRef.current = null;
      const [p1, p2] = Array.from(activePointersRef.current.values());
      pinchDistanceRef.current = Math.hypot(p1.x - p2.x, p1.y - p2.y);
    }
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const pointers = activePointersRef.current;
      if (!pointers.has(e.pointerId)) return;
      const prevPos = pointers.get(e.pointerId)!;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size === 2 && pinchDistanceRef.current !== null) {
        const [p1, p2] = Array.from(pointers.values());
        const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        if (distance > 0 && pinchDistanceRef.current > 0) {
          const midX = (p1.x + p2.x) / 2;
          const midY = (p1.y + p2.y) / 2;
          zoomAt(midX, midY, distance / pinchDistanceRef.current);
        }
        pinchDistanceRef.current = distance;
        return;
      }

      if (panPointerRef.current === e.pointerId) {
        const dx = e.clientX - prevPos.x;
        const dy = e.clientY - prevPos.y;
        setViewport((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
      }
    },
    [zoomAt],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    activePointersRef.current.delete(e.pointerId);
    if (panPointerRef.current === e.pointerId) {
      panPointerRef.current = null;
      setIsPanning(false);
    }
    if (activePointersRef.current.size < 2) {
      pinchDistanceRef.current = null;
    }
  }, []);

  // ===== アイテム操作 =====

  // 選択時にコンテナへフォーカスを移し、Delete キーを受け取れるようにする。
  // `additive`（Shift クリック）は既存の選択へ追加 / 解除する。
  const handleSelect = useCallback((id: string, additive: boolean) => {
    setSelectedIds((prev) => {
      if (!additive) {
        return prev.length === 1 && prev[0] === id ? prev : [id];
      }
      return prev.includes(id)
        ? prev.filter((prevId) => prevId !== id)
        : [...prev, id];
    });
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  const handleCommitGeometry = useCallback(
    (itemId: string, patch: GeometryPatch) => {
      if (readOnly) return;
      updateItem({ note_id: noteId, item_id: itemId, ...patch })
        .unwrap()
        .catch(() => toast.error("保存に失敗しました"));
    },
    [readOnly, noteId, updateItem],
  );

  /** 選択中のアイテムをまとめて削除する */
  const handleDeleteSelection = useCallback(() => {
    if (readOnly || selectedItems.length === 0) return;
    setSelectedIds([]);
    setIsConfirmingDelete(false);
    Promise.all(
      selectedItems.map((item) =>
        deleteItem({ note_id: noteId, item_id: item.id }).unwrap(),
      ),
    ).catch(() => toast.error("削除に失敗しました"));
  }, [readOnly, noteId, selectedItems, deleteItem]);

  // 選択中の画像をコピーする（⌘C / ツールバー）。
  // - ボード内での貼り付け用に、S3 キーとボード上のサイズを内部クリップボードへ控える
  // - 他アプリへ持ち出せるよう、システムクリップボードへも PNG を書き込む
  //   （1 枚なら PNG へ正規化、複数ならボード上の配置を保った 1 枚に合成）
  // PNG の取得や書き込みに失敗した場合（CORS・権限等）は画像 URL の
  // 文字列コピーにフォールバックする。
  const handleCopySelection = useCallback(() => {
    const targets = selectedItems.filter((item) => !!item.image_url);
    if (targets.length === 0) return;
    const isSingle = targets.length === 1;

    // 重なり順を保って貼り付けられるよう z_index 昇順で控える。
    const ordered = [...targets].sort((a, b) => a.z_index - b.z_index);
    const minX = Math.min(...ordered.map((item) => item.x));
    const minY = Math.min(...ordered.map((item) => item.y));
    writeBoardClipboard(
      noteId,
      ordered.map((item) => ({
        image_key: item.image_key,
        width: item.width,
        height: item.height,
        offsetX: item.x - minX,
        offsetY: item.y - minY,
      })),
    );

    const pngBlobPromise = isSingle
      ? fetchAsPng(targets[0].image_url)
      : composeItemsToPng(targets);

    // 画像として書き込めない場合の最終フォールバック（URL 文字列コピー）
    const copyUrlsAsText = () =>
      navigator.clipboard
        .writeText(targets.map((item) => item.image_url).join("\n"))
        .then(() =>
          toast.success(
            isSingle
              ? "画像 URL をコピーしました"
              : `${targets.length}件の画像 URL をコピーしました`,
          ),
        )
        .catch(() => toast.error("コピーに失敗しました"));

    // Safari はユーザー操作から同期的に write が呼ばれる必要があるため、
    // Blob の解決を待たず Promise を ClipboardItem に直接渡す
    try {
      navigator.clipboard
        .write([new ClipboardItem({ "image/png": pngBlobPromise })])
        .then(() =>
          toast.success(
            isSingle
              ? "画像をコピーしました"
              : `${targets.length}枚の画像を1枚に合成してコピーしました`,
          ),
        )
        .catch(copyUrlsAsText);
    } catch {
      // ClipboardItem 未対応環境。取得中の Promise は握りつぶして URL コピーへ
      pngBlobPromise.catch(() => {});
      copyUrlsAsText();
    }
  }, [selectedItems, noteId]);

  // Delete / Backspace: 選択中のアイテムを削除、⌘C / Ctrl+C: クリップボードへコピー
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === "Escape" && isDrawingMode) {
        e.preventDefault();
        setTool("select");
        return;
      }
      if (selectedCount === 0) return;
      if ((e.key === "Delete" || e.key === "Backspace") && !readOnly) {
        e.preventDefault();
        // 1 枚だけの削除は従来どおり即時、複数枚は取り消せないため確認する
        if (selectedCount === 1) handleDeleteSelection();
        else setIsConfirmingDelete(true);
        return;
      }
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        e.key.toLowerCase() === "c"
      ) {
        // テキスト選択中は通常のコピー動作を優先する
        if (window.getSelection()?.toString()) return;
        e.preventDefault();
        handleCopySelection();
      }
    },
    [
      selectedCount,
      isDrawingMode,
      readOnly,
      handleDeleteSelection,
      handleCopySelection,
    ],
  );

  // ドラッグ & ドロップ
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0 || readOnly) return;
      addImageFiles(files, screenToWorld(e.clientX, e.clientY));
    },
    [readOnly, addImageFiles, screenToWorld],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (files.length > 0) {
        addImageFiles(files);
      }
    },
    [addImageFiles],
  );

  // 注意: ローディング中でも BoardWrapper 自体は必ずレンダリングする。
  // 早期 return すると、マウント時に 1 回だけ走る effect（wheel リスナー登録・
  // 初期ビューポート設定）が containerRef 未設定のまま実行されてしまうため。
  // 選択中のツールは IconButton に isActive が無いため、色と FILL で示す。
  // 描画モードへ移るときは画像の選択を解除する（Delete で意図せず画像が
  // 消えるのを防ぐ。描画中はハンドルも邪魔になる）。
  const toolButtonProps = (value: BoardTool) => ({
    color: tool === value ? "var(--soremo-blue)" : undefined,
    filled: tool === value,
    onClick: () => {
      setTool(value);
      if (value !== "select") setSelectedIds([]);
    },
  });

  // 描画で処理しなかった入力（指）はキャンバスのパン / ピンチへ回す。
  const drawingSurfaceHandlers = createDrawingSurfaceHandlers(drawing, {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
  });

  const isEmpty = !isLoading && items.length === 0 && pendingItems.length === 0;
  const zoomPercent = Math.round(viewport.scale * 100);

  return (
    <>
      <BoardWrapper
        ref={containerRef}
        className={isPanning ? "is-panning" : undefined}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={handleKeyDown}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        style={{
          backgroundSize: `${24 * viewport.scale}px ${24 * viewport.scale}px`,
          backgroundPosition: `${viewport.x}px ${viewport.y}px`,
        }}
      >
        <World
          style={{
            transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
          }}
        >
          {items.map((item) => (
            <BoardImageItem
              key={item.id}
              item={item}
              scale={viewport.scale}
              selected={selectedIdSet.has(item.id)}
              showHandles={selectedCount === 1}
              readOnly={readOnly}
              onSelect={handleSelect}
              onCommitGeometry={handleCommitGeometry}
            />
          ))}

          <BoardStrokeLayer
            strokes={strokes}
            livePoints={drawing.livePoints}
            liveColor={penColor}
            liveWidth={penWidth}
            pendingEraseIds={drawing.pendingEraseIds}
          />

          {pendingItems.map((pending) => (
            <PendingPlaceholder
              key={pending.id}
              style={{
                transform: `translate(${pending.x}px, ${pending.y}px)`,
                width: pending.width,
                height: pending.height,
              }}
            >
              <img src={pending.objectUrl} alt="" />
            </PendingPlaceholder>
          ))}
        </World>

        {/*
          描画モード中だけ被せる。指（touch）はここでは処理せず、キャンバスの
          パン / ピンチ用ハンドラへそのまま渡す。

          描画で処理した入力は stopPropagation で必ず止める。親の BoardWrapper
          にもパン用の pointerdown ハンドラがあり、そちらが自分へ
          setPointerCapture してしまうと以降の pointermove が描画側へ届かず、
          点が 1 つしか取れずに線が消える。
        */}
        {isDrawingMode && <DrawingSurface {...drawingSurfaceHandlers} />}

        {isLoading && <SvgLoadingAnimation />}

        {isEmpty && (
          <EmptyGuide>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: "64px", color: "var(--soremo-light-gray)" }}
            >
              imagesmode
            </span>
            <div className="u-col-center u-gap8">
              <p className="heading-spacing">{title || "イメージボード"}</p>
              {readOnly ? (
                <p className="bodytext-13 u-text-light-gray u-text-center">
                  まだ画像がありません。
                </p>
              ) : (
                <p className="bodytext-13 u-text-light-gray u-text-center">
                  クリップボードから貼り付け（⌘V）またはドラッグ&ドロップで
                  <br />
                  画像を追加して、メンバーとイメージを共有しましょう。
                  <br />
                  Shift + クリックで複数選択して、まとめてコピーできます。
                </p>
              )}
            </div>
          </EmptyGuide>
        )}

        {/* ツールバー操作がキャンバスのパン開始・選択解除に伝播しないようにする */}
        <FloatingToolbar onPointerDown={(e) => e.stopPropagation()}>
          {!readOnly && (
            <>
              <IconButton
                icon="add_photo_alternate"
                title="画像を追加"
                ariaLabel="画像を追加"
                onClick={() => fileInputRef.current?.click()}
              />

              <ToolbarDivider />

              <IconButton
                icon="arrow_selector_tool"
                title="選択（画像を動かす）"
                ariaLabel="選択モード"
                {...toolButtonProps("select")}
              />
              <IconButton
                icon="draw"
                title="ペン（Apple Pencil で描けます。指ではキャンバスを動かせます）"
                ariaLabel="ペンモード"
                {...toolButtonProps("pen")}
              />
              <IconButton
                icon="ink_eraser"
                title="消しゴム（なぞった線を消します）"
                ariaLabel="消しゴムモード"
                {...toolButtonProps("eraser")}
              />
            </>
          )}

          {tool === "pen" && (
            <>
              {PEN_COLORS.map((option) => (
                <ColorSwatch
                  key={option.value}
                  type="button"
                  $color={option.value}
                  $selected={penColor === option.value}
                  aria-label={`ペンの色: ${option.label}`}
                  aria-pressed={penColor === option.value}
                  onClick={() => setPenColor(option.value)}
                />
              ))}
              {PEN_WIDTHS.map((option) => (
                <WidthSwatch
                  key={option}
                  type="button"
                  $selected={penWidth === option}
                  aria-label={`ペンの太さ: ${option}`}
                  aria-pressed={penWidth === option}
                  onClick={() => setPenWidth(option)}
                >
                  <span
                    style={{
                      width: `${Math.min(option, 14)}px`,
                      height: `${Math.min(option, 14)}px`,
                    }}
                  />
                </WidthSwatch>
              ))}
            </>
          )}

          {isDrawingMode && (
            <IconButton
              icon="undo"
              title="直前に描いた線を取り消す"
              ariaLabel="描いた線を取り消す"
              disabled={!drawing.canUndo}
              onClick={drawing.undo}
            />
          )}

          {!readOnly && <ToolbarDivider />}
          {selectedCount > 0 && (
            <>
              <IconButton
                icon="content_copy"
                title={
                  selectedCount === 1
                    ? "選択中の画像をコピー（⌘V で同じ大きさのまま複製）"
                    : `選択中の${selectedCount}枚をコピー（⌘V で配置ごと複製）`
                }
                ariaLabel="選択中の画像をコピー"
                onClick={handleCopySelection}
              />
              {selectedCount === 1 && (
                <CopyLinkButton
                  url={() =>
                    buildImageBoardItemLink(noteId, selectedItems[0].id, linkProjectId)
                  }
                  label={`${title || "イメージボード"}の画像`}
                  size="large"
                  title="選択中の画像へのリンクをコピー"
                />
              )}
              {!readOnly && (
                <IconButton
                  icon="delete"
                  title={
                    selectedCount === 1
                      ? "選択中の画像を削除"
                      : `選択中の${selectedCount}枚を削除`
                  }
                  ariaLabel="選択中の画像を削除"
                  onClick={() => setIsConfirmingDelete(true)}
                />
              )}
            </>
          )}
          {selectedCount === 0 && (
            <CopyLinkButton
              url={() => {
                const center = viewportCenterWorld();
                return buildImageBoardViewLink(
                  noteId,
                  {
                    x: center.x,
                    y: center.y,
                    scale: viewportRef.current.scale,
                  },
                  linkProjectId,
                );
              }}
              label={`${title || "イメージボード"}の位置`}
              size="large"
              title="この表示位置へのリンクをコピー"
            />
          )}
          <IconButton
            icon="remove"
            title="縮小"
            ariaLabel="縮小"
            onClick={() => zoomAtCenter(1 / ZOOM_BUTTON_STEP)}
          />
          <ToolTip description="ズームをリセット">
            <ZoomLabel
              className="bodytext-13 u-text-light-gray"
              onClick={resetViewport}
            >
              {zoomPercent}%
            </ZoomLabel>
          </ToolTip>
          <IconButton
            icon="add"
            title="拡大"
            ariaLabel="拡大"
            onClick={() => zoomAtCenter(ZOOM_BUTTON_STEP)}
          />
        </FloatingToolbar>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileSelect}
        />
      </BoardWrapper>

      {isConfirmingDelete && selectedCount > 0 && (
        <ConfirmModal
          message={
            selectedCount === 1
              ? "画像を削除しますか？"
              : `選択中の${selectedCount}枚の画像を削除しますか？`
          }
          confirmLabel="削除"
          onConfirm={handleDeleteSelection}
          onCancel={() => setIsConfirmingDelete(false)}
        />
      )}

      {/* アップロード部分失敗ダイアログ */}
      {uploadFailureDialog}
    </>
  );
};

export default ImageBoard;
